import React from 'react';
import classnames from 'classnames/bind';

import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';

import styles from './Dropzone.module.scss';

const c = classnames.bind(styles);

type DropzoneProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> & {
  onChange: (files: File[]) => void;
};

export function Dropzone({
  className,
  onChange,
  accept,
  multiple,
  ...props
}: DropzoneProps) {
  const [isDragging, setIsDragging] = React.useState(false);

  React.useEffect(() => {
    // Prevent browser opening file on drop
    ['dragenter', 'dragover', 'drop'].forEach((event) =>
      window.addEventListener(event, preventDefault)
    );

    return () => {
      ['dragenter', 'dragover', 'drop'].forEach((event) =>
        window.removeEventListener(event, preventDefault)
      );
    };
  }, []);

  function handleDrop(event: React.DragEvent<HTMLLabelElement>) {
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);

    if (accept && files.length) {
      const regex = new RegExp(accept);

      onChange(files.filter((file) => regex.test(file.type)));
      return;
    }

    onChange(files);
  }

  return (
    <label
      className={c(className, 'dropzone', 'theme info', {
        active: isDragging,
      })}
      onDragEnter={() => setIsDragging(true)}
      onDragLeave={() => setIsDragging(true)}
      onDrop={handleDrop}
    >
      <Icon name="upload" size="large" />

      <input
        {...props}
        className="sr-only"
        type="file"
        accept={accept}
        onChange={({ target: { files } }) =>
          files && onChange(Array.from(files))
        }
      />

      <Heading level="4">
        Drag and drop or{' '}
        <span className="link">choose {multiple ? 'files' : 'file'}</span>
      </Heading>
    </label>
  );
}

function preventDefault(event: Event) {
  event.preventDefault();
}
